import React, { createContext, useReducer, useContext } from "react";
import { getDefaultDateSelector } from "./DatesSelector";
import { parseISO } from "date-fns";
import { datesStringToDatesArray, parseDogIdsParam } from "../../lib/utils";
import { getDefaultTimeSelector } from "./TimesSelector";
import {
  DatesSelectorValue,
  CountryServiceType,
  QueryType,
  TimeSelectorValue
} from "../../interfaces";
import { PickupAtHomeValue } from "./PickupAtHomeSelector";
import { CurrentUser, Caregiver, Dog } from "../../generated/graphql";

type Action =
  | {
      type: "changeCountryServiceType";
      payload: { countryServiceType: CountryServiceType };
    }
  | {
      type: "changeDatesSelectorValue";
      payload: { datesSelectorValue: DatesSelectorValue };
    }
  | {
      type: "changeDogsSelectorValue";
      payload: { dogsSelectorValue: string[] };
    }
  | {
      type: "changeTimeSelectorValue";
      payload: { timeSelectorValue: TimeSelectorValue };
    }
  | {
      type: "changePickUpAtHomeValue";
      payload: { pickUpAtHomeValue: PickupAtHomeValue };
    }
  | {
      type: "changeMeetAndGreetValue";
      payload: { meetAndGreetValue: boolean };
    }
  | {
      type: "changeMessageValue";
      payload: { messageValue: string };
    }
  | {
      type: "changePhoneValue";
      payload: { phoneValue: string };
    };
type Dispatch = (action: Action) => void;

interface State {
  countryServiceType: CountryServiceType;
  datesSelectorValue: DatesSelectorValue;
  timeSelectorValue: TimeSelectorValue;
  pickUpAtHomeValue: PickupAtHomeValue;
  meetAndGreetValue: boolean;
  messageValue: string;
  phoneValue: string;
  dogsSelectorValue: string[];
}
interface SitterContactProviderProps {
  children: React.ReactNode;
  initialStateFromQuery: State;
}

const SitterContactStateContext = createContext<State | undefined>(undefined);
const SitterContactDispatchContext = createContext<Dispatch | undefined>(
  undefined
);

function getInitialStateFromQuery(
  query: QueryType,
  caregiver: Caregiver,
  user: Pick<CurrentUser, "phone"> & {
    dogs?: Pick<Dog, "id">[];
  }
): State {
  let datesSelectorValue: DatesSelectorValue;
  let countryServiceType: CountryServiceType;
  let timeSelectorValue: TimeSelectorValue;
  let pickUpAtHomeValue: PickupAtHomeValue;
  let meetAndGreetValue: boolean;
  let messageValue: string;
  let phoneValue: string;
  let dogsSelectorValue: string[];

  if (!user) return null;

  if (query.serviceTypeId) {
    const service = caregiver.services.filter(function(val) {
      return val.countryServiceType.serviceType.id == query.serviceTypeId;
    });

    if (service) {
      countryServiceType = service[0].countryServiceType;
    }
  }

  if (!countryServiceType) {
    countryServiceType = caregiver.services[0].countryServiceType;
  }

  datesSelectorValue = getDefaultDateSelector(countryServiceType.serviceType);

  if (query.serviceDates) {
    datesSelectorValue = {
      kind: "multipleDates",
      dates: datesStringToDatesArray(query.serviceDates),
      startDate: undefined,
      endDate: undefined
    };
  } else if (query.startDate && query.endDate) {
    datesSelectorValue = {
      kind: "dateRange",
      startDate: parseISO(query.startDate),
      endDate: parseISO(query.endDate),
      dates: []
    };
  } else {
    datesSelectorValue = getDefaultDateSelector(countryServiceType.serviceType);
  }

  pickUpAtHomeValue = {
    pickUpAtHome: false,
    numberOfTrips: 1
  };
  meetAndGreetValue = false;
  messageValue = "";
  phoneValue = user?.phone;
  timeSelectorValue = getDefaultTimeSelector(countryServiceType.serviceType);

  if (query.dogIds) {
    const dogIdsArray = parseDogIdsParam(query.dogIds);
    dogsSelectorValue = user.dogs.map(dog => {
      return dogIdsArray.filter(dogId => {
        return dog.id == dogId;
      })[0];
    });
  } else {
    dogsSelectorValue = user.dogs.map(dog => {
      return dog.id;
    });
  }

  return {
    countryServiceType,
    datesSelectorValue,
    timeSelectorValue,
    pickUpAtHomeValue,
    meetAndGreetValue,
    messageValue,
    phoneValue,
    dogsSelectorValue
  };
}

function sitterContactReducer(state: State, action: Action) {
  switch (action.type) {
    case "changePhoneValue":
    case "changeMessageValue":
    case "changePickUpAtHomeValue":
    case "changeMeetAndGreetValue":
    case "changeDatesSelectorValue":
    case "changeTimeSelectorValue":
    case "changeDogsSelectorValue":
    case "changeCountryServiceType": {
      return {
        ...state,
        ...action.payload
      };
    }

    default:
      throw new Error();
  }
}

function SitterContactProvider({
  initialStateFromQuery,
  children
}: SitterContactProviderProps) {
  const [state, dispatch] = useReducer(
    sitterContactReducer,
    initialStateFromQuery
  );

  return (
    <SitterContactStateContext.Provider value={state}>
      <SitterContactDispatchContext.Provider value={dispatch}>
        {children}
      </SitterContactDispatchContext.Provider>
    </SitterContactStateContext.Provider>
  );
}

SitterContactProvider.defaultProps = {
  initialStateFromQuery: {}
};

function useSitterContactState() {
  const context = useContext(SitterContactStateContext);

  if (context === undefined) {
    throw new Error(
      "useSitterContactState must be used within a SitterContactProvider"
    );
  }

  return context;
}

function useSitterContactDispatch() {
  const context = useContext(SitterContactDispatchContext);
  if (context === undefined) {
    throw new Error(
      "useSitterContactDispatch must be used within a SitterContactProvider"
    );
  }
  return context;
}

export {
  SitterContactProvider,
  useSitterContactState,
  useSitterContactDispatch,
  getInitialStateFromQuery
};
